/**
 * Authenticated app chrome (ARCHITECTURE.md §8).
 *
 * A persistent left "console" rail + a thin top bar around the routed `<Outlet>`.
 *
 * Signature element — the **arm state module** pinned to the top of the rail.
 * §0 safety invariant #5 says the system ships disarmed with dry-run on and no
 * money moves silently; this shell makes that state *ambient*, not buried in
 * Settings: the operator always sees whether live trading is armed and whether
 * orders are simulated. Everything else in the chrome stays deliberately quiet
 * so this one indicator carries the weight.
 */
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { cn } from "@richrich/ui";
import { useI18n } from "@richrich/i18n/react";
import { api, unwrap } from "../lib/api";
import { queryClient, sessionQuery, settingsQuery } from "../lib/query";
import { toggleTheme, storedTheme } from "../lib/theme";
import { FOOTER_NAV, isLocked, PRIMARY_NAV, type NavItem } from "../lib/nav";
import { useLiveEvents } from "../features/live/useLiveEvents";
import { LockIcon, LogoutIcon, MoonIcon, SunIcon } from "./icons";
import { useState, type ReactNode } from "react";
import type { FeatureFlags } from "@richrich/contracts";

function NavRow({ item, features }: { item: NavItem; features: FeatureFlags | undefined }) {
  const { t } = useI18n();
  const locked = isLocked(item, features);
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      activeOptions={{ exact: item.to === "/" }}
      activeProps={{ "data-active": "true" }}
      className={cn(
        "group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm",
        "text-ink-secondary transition-colors hover:bg-elevated hover:text-ink",
        "data-[active=true]:bg-elevated data-[active=true]:text-ink",
      )}
    >
      {/* Brand accent bar marks the active section. */}
      <span
        aria-hidden
        className={cn(
          "absolute left-0 top-1/2 h-4 w-[3px] -translate-y-1/2 rounded-full bg-brand",
          "opacity-0 transition-opacity group-data-[active=true]:opacity-100",
        )}
      />
      <Icon className="shrink-0 text-muted group-data-[active=true]:text-brand" />
      <span className="flex-1 truncate">{t(item.labelKey)}</span>
      {locked ? <LockIcon className="text-muted" aria-label={t("nav.setupRequired")} /> : null}
    </Link>
  );
}

function ArmState({ armed, dryRun }: { armed: boolean | undefined; dryRun: boolean | undefined }) {
  const { t } = useI18n();
  const isArmed = armed === true;
  return (
    <div
      className={cn(
        "rounded-md border px-3 py-2.5",
        isArmed ? "border-critical/40 bg-critical/5" : "border-hairline bg-surface",
      )}
    >
      <div className="flex items-center gap-2">
        <span
          aria-hidden
          className={cn("h-2 w-2 rounded-full", isArmed ? "animate-pulse bg-critical" : "bg-muted")}
        />
        <span
          className={cn(
            "font-mono text-[11px] font-semibold uppercase tracking-[0.14em]",
            isArmed ? "text-critical" : "text-muted",
          )}
        >
          {armed === undefined
            ? t("common.loading")
            : isArmed
              ? t("nav.tradingArmed")
              : t("nav.disarmed")}
        </span>
      </div>
      <div className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-muted">
        {dryRun === false ? t("nav.liveOrders") : t("nav.dryRunSimulated")}
      </div>
    </div>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const session = useQuery(sessionQuery);
  const settings = useQuery(settingsQuery);
  const [theme, setThemeState] = useState(() => storedTheme());

  // The one app-wide live-events socket — the shell renders only for
  // authenticated routes (see routes/__root.tsx), so this is the auth gate.
  useLiveEvents();

  const logout = useMutation({
    mutationFn: async () => unwrap(await api.auth.logout.$post()),
    onSuccess: async () => {
      queryClient.clear();
      await navigate({ to: "/login" });
    },
  });

  const features = settings.data?.features;
  const email = session.data?.user?.email ?? "—";

  return (
    <div className="flex h-full min-h-0 bg-page text-ink">
      <aside className="flex w-[var(--spacing-rail)] shrink-0 flex-col border-r border-hairline bg-surface">
        {/* Wordmark */}
        <div className="flex items-center gap-2 px-5 py-4">
          <span className="font-mono text-sm font-semibold tracking-[0.2em] text-ink">
            rich<span className="text-brand">rich</span>
          </span>
        </div>

        <div className="px-3">
          <ArmState armed={settings.data?.tradingArmed} dryRun={settings.data?.defaultDryRun} />
        </div>

        <nav className="mt-4 flex flex-1 flex-col gap-0.5 overflow-y-auto px-3">
          {PRIMARY_NAV.map((item) => (
            <NavRow key={item.to} item={item} features={features} />
          ))}
          <div className="mt-auto flex flex-col gap-0.5 pt-4">
            {FOOTER_NAV.map((item) => (
              <NavRow key={item.to} item={item} features={features} />
            ))}
          </div>
        </nav>

        {/* Operator / session footer */}
        <div className="border-t border-hairline px-3 py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate font-mono text-xs text-ink-secondary" title={email}>
                {email}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-muted">
                {t("nav.owner")}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setThemeState(toggleTheme())}
                className="rounded-md p-1.5 text-muted transition-colors hover:bg-elevated hover:text-ink"
                aria-label={theme === "dark" ? t("nav.switchToLight") : t("nav.switchToDark")}
                title={t("nav.toggleTheme")}
              >
                {theme === "dark" ? <SunIcon /> : <MoonIcon />}
              </button>
              <button
                type="button"
                onClick={() => logout.mutate()}
                disabled={logout.isPending}
                className="rounded-md p-1.5 text-muted transition-colors hover:bg-elevated hover:text-critical disabled:opacity-50"
                aria-label={t("nav.signOut")}
                title={t("nav.signOut")}
              >
                <LogoutIcon />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Content column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <main className="min-h-0 flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
